import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Shield, Lock, CreditCard } from 'lucide-react';
import { TranslatedText } from '@/components/TranslatedText';
import { ModernPricingPage, PricingCardProps } from '@/components/ui/animated-glassy-pricing';
import { useAdminSettings } from '@/hooks/useAdminSettings';
import { trackCTAClick } from '@/lib/metaPixel';

export const LightPricingSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const { monthlyPrice, lifetimePrice } = useAdminSettings();

  const pricingPlans: PricingCardProps[] = [
    {
      planName: 'Free Trial',
      description: <>Explore the platform and see how everything works.</>,
      price: '0',
      priceLabel: ' free',
      features: [
        'Access to lessons, tools, and AI teacher',
        'Generate your first content with 10 free credits',
        'Test scripts, captions, images, and niche ideas',
        'No credit card required',
      ],
      buttonText: 'Start Free — 10 Credits Included',
      buttonVariant: 'secondary',
      onClick: () => { trackCTAClick('Start Free', 'Pricing-Free'); window.location.href = '/apply?plan=free'; },
    },
    {
      planName: 'Monthly Access',
      description: <>Everything in Free, plus enough credits to build and test consistently.</>,
      price: monthlyPrice,
      priceLabel: '/mo',
      originalPrice: (parseFloat(monthlyPrice) * 2).toFixed(2),
      discountBadge: '50% OFF — Limited Time',
      features: [
        '100 AI credits included',
        '100 new credits every month',
        'Ask your AI teacher anything',
        'Full access to all lessons and workflows',
        'Cancel Anytime',
      ],
      footerNote: 'Perfect for people actively building projects.',
      buttonText: 'Get Monthly Access',
      isPopular: true,
      buttonVariant: 'primary',
      onClick: () => { trackCTAClick('Get Monthly Access', 'Pricing-Monthly'); window.location.href = '/apply?plan=monthly'; },
    },
    {
      planName: 'Lifetime Access',
      description: 'Everything in Monthly, plus a large credit pack to start building immediately.',
      price: lifetimePrice,
      priceLabel: ' one-time',
      originalPrice: (parseFloat(lifetimePrice) * 2).toFixed(0),
      discountBadge: '50% OFF — Limited Time',
      features: [
        '500 AI credits included',
        'Permanent platform access',
        'All future updates included',
        'Private community access',
        '7-Day Money-Back Guarantee',
      ],
      footerNote: 'Pay once. Build forever.',
      buttonText: 'Get Lifetime Access',
      isPopular: false,
      buttonVariant: 'secondary',
      onClick: () => { trackCTAClick('Get Lifetime Access', 'Pricing-Lifetime'); window.location.href = '/apply?plan=lifetime'; },
    },
  ];

  return (
    <section ref={ref} id="pricing">
      <ModernPricingPage
        title={
          <TranslatedText>Choose Your Access Level</TranslatedText>
        }
        subtitle={
          <>
            <span className="block mb-1"><TranslatedText>Choose how you want to start building.</TranslatedText></span>
            <span className="text-muted-foreground"><TranslatedText>Start with 1 free class — no credit card required.</TranslatedText></span>
          </>
        }
        plans={pricingPlans}
        showAnimatedBackground={true}
      />

      {/* Trust elements */}
      <div className="bg-background px-4 pb-12 sm:pb-16">
        <div className="max-w-lg mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex items-center justify-center gap-3 flex-wrap mb-4"
          >
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-muted rounded-full">
              <Lock className="w-3.5 h-3.5 text-green-600" />
              <span className="text-xs text-muted-foreground font-medium">
                <TranslatedText>SSL Secure</TranslatedText>
              </span>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-muted rounded-full">
              <CreditCard className="w-3.5 h-3.5 text-primary" />
              <span className="text-xs text-muted-foreground font-medium">
                <TranslatedText>Stripe Payments</TranslatedText>
              </span>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-muted rounded-full">
              <Shield className="w-3.5 h-3.5 text-green-600" />
              <span className="text-xs text-muted-foreground font-medium">
                <TranslatedText>7-Day Guarantee</TranslatedText>
              </span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="flex items-center justify-center gap-3 text-muted-foreground mb-8"
          >
            <span className="text-xs font-bold tracking-wide">VISA</span>
            <span className="text-xs font-bold tracking-wide">Mastercard</span>
            <span className="text-xs font-bold tracking-wide">AMEX</span>
            <span className="text-xs font-bold tracking-wide">Apple Pay</span>
            <span className="text-xs font-bold tracking-wide">Google Pay</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="p-5 rounded-xl bg-green-500/10 border border-green-500/20 flex items-start gap-4"
          >
            <Shield className="w-10 h-10 text-green-600 dark:text-green-400 flex-shrink-0" />
            <div>
              <h4 className="font-bold text-green-700 dark:text-green-400 mb-1">
                <TranslatedText>7-Day Guarantee</TranslatedText>
              </h4>
              <p className="text-green-700 dark:text-green-300 text-sm">
                <TranslatedText>Not satisfied? Get a full refund. No questions asked.</TranslatedText>
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default LightPricingSection;
